'use client'

import { useContext } from 'react'
import type { TypeDuplexHeroBlockFields } from '@lib/generated-types'
import { DuplexHeroBlock as Block } from '@shc/ui'
import useAnalytics, { type SPEvent, type SPContext } from '@hooks/use-analytics'
import { BlockContext } from '@lib/analytics'
import { routeMapper } from '@lib/menu-item-mapper'
import { richTextRender } from '@lib/rich-text-base'
import { pyMap } from '@lib/token-maps'
import slugify from 'slugify'

const DuplexHeroBlock = ({
  sectionLink,
  heading,
  description,
  image,
  button1Label,
  button1Link,
  button1AnchorOrExternalLink,
  button2Label,
  button2Link,
  button2AnchorOrExternalLink,
  paddingTop,
  paddingBottom,
  preheading,
}: TypeDuplexHeroBlockFields) => {
  const btnLink1 = routeMapper(button1Link, button1AnchorOrExternalLink)
  const btnLink2 = routeMapper(button2Link, button2AnchorOrExternalLink)
  const imageFields = image.fields.image.fields
  const { track } = useAnalytics()
  const blockContext = useContext(BlockContext)

  const handleBtnAnalytics = (label: string | undefined, link: string) => {
    track({
      event: {
        name: 'component_click',
        data: {}
      } as SPEvent,
      contexts: [
        {
          name: 'component',
          data: {
            component_text: label,
            component_url: link,
          },
        },
        blockContext,
      ] as SPContext[],
    })
  }

  return (
    <Block
      buttonPrimary={(button1Link || button1AnchorOrExternalLink) && button1Label ? {
        href: btnLink1,
        label: button1Label,
        onClick: () => handleBtnAnalytics(button1Label, btnLink1.route),
        rel: btnLink1.isInternal ? undefined : 'noopener noreferrer',
        target: btnLink1.isInternal ? undefined : '_blank',
      } : undefined }
      buttonSecondary={(button2Link || button2AnchorOrExternalLink) && button2Label ? {
        href: btnLink2,
        label: button2Label,
        onClick: () => handleBtnAnalytics(button2Label, btnLink2.route),
        rel: btnLink2.isInternal ? undefined : 'noopener noreferrer',
        target: btnLink2.isInternal ? undefined : '_blank',
      } : undefined }
      className="duplex-block"
      description={
        <>{richTextRender(description)}</>
      }
      heading={heading}
      /* Using default image props bc sharp-ui and Next Image sizes prop
         do not appear compatible at the moment at VWs < 1024px */
      image={{
        alt: imageFields.title,
        src: imageFields.file.url,
      }}
      id={typeof sectionLink === 'undefined' ? undefined : slugify(sectionLink)}
      paddingBottom={pyMap[paddingBottom]}
      paddingTop={pyMap[paddingTop]}
      preHeading={preheading}>
    </Block>
  )
}

export default DuplexHeroBlock
